import qs from 'qs';

import {
  AssetsHttpGetCollectionsByCollectionIdContents,
  httpGetCollectionsByCollectionId,
  httpGetCollectionsByCollectionIdContents,
  httpPutCollectionsByCollectionId,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  httpGetSearchSaved,
  httpGetSearchSavedBySearchId,
  httpPostSearch,
  SearchHttpPostSearch,
  type SearchSearchViewFieldTypeSchema,
} from '@iconik-io/iconik-api/src/services/search';
import { OBJECT_TYPE_COLLECTIONS } from '@iconik-io/iconik-api/src/types/object_types';
import {
  type CommonListGetParams,
  commonGetList,
} from '@iconik-io/shared-lib/src/redux/common_list/thunks';
import type { EndpointResponseNormalized } from '@iconik-io/shared-lib/src/redux/middlewares/http_normalizer/types';
import { getSortQuery } from '@iconik-io/shared-lib/src/utils/sort/get_sort_query';
import { toSortOrder } from '@iconik-io/shared-lib/src/utils/sort/types';
import { getQueryPage } from '@iconik-io/shared-lib/src/utils/url/get_query_page';
import { getQueryPerPage } from '@iconik-io/shared-lib/src/utils/url/get_query_per_page';
import { getQuerySearch } from '@iconik-io/shared-lib/src/utils/url/get_query_search';

import {
  selectMetadataMultiSelectFieldNames,
  selectVisibleSearchFieldNames,
} from '../../../components/search_items/selectors';
import { CUSTOM_ORDER } from '../../../components/search_settings/sorting_selector/constants';
import { PAGE_SIZE_ITEM } from '../../../constants/local_storage';
import { ID_COLLECTION } from '../../../constants/reducer_ids';
import {
  COLLECTION_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
} from '../../../constants/routes';
import type { AppDispatch, GetState } from '../../../store/types';
import { criteriaToFilterValues } from '../../../utils/search_utils/criteria_to_filter_values';
import { getCollectionParents } from '../../collections/thunks';
import { getEntityCurrentAcls } from '../../common_acls/thunks';
import { historyPush } from '../../history/actions';
import { cancellable } from '../../http/actions';
import { selectIsNaturalSearchActive } from '../../natural_search/selectors';
import { endNaturalSearchSession } from '../../natural_search/session';
import {
  loadMoreNaturalSearch,
  refreshNaturalSearch,
} from '../../natural_search/thunks';
import { searchFilterActions } from '../../panels/search_filters/actions';
import { updateSavedSearch } from '../../saved_search/thunks';
import {
  getSearchRequestBody,
  preprocessSortSettings,
} from '../../search/thunks';
import { modifyBranchByID } from '../../tree/thunks';
import { getMetadataFields } from '../admin_metadata_fields/thunks';
import { searchActions } from './actions';
import { SEARCH_LIST_NAME } from './list_slice';
import {
  selectIsSearchPagedByNumber,
  selectSearchPaginationType,
} from './pagination';
import { selectCanResumeSearchList } from './selectors';
import { collectMetadataFieldNames, criteriaToFacetValues } from './utils';

const forceCustomSortRoutes = new Set<string>([
  COLLECTION_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
]);

const requestSearchItems =
  ({ page, search_after }: CommonListGetParams = {}) =>
  (dispatch: AppDispatch, getState: GetState) => {
    dispatch(preprocessSortSettings());
    const state = getState();

    const searchParams = state.router.location.search;
    const perPage = getQueryPerPage(searchParams, PAGE_SIZE_ITEM);
    const isPagedByNumber = selectIsSearchPagedByNumber(state);
    const pageNumber = isPagedByNumber
      ? getQueryPage(searchParams)
      : (page ?? 1);

    const searchRequest = cancellable(
      httpPostSearch(
        { ...getSearchRequestBody(state), search_after },
        isPagedByNumber
          ? { page: pageNumber, per_page: perPage }
          : { per_page: perPage },
      ),
    );

    // A share page lists the collection's contents instead of searching, and
    // the two endpoints answer with different bodies. The caller dispatches
    // whichever it gets and reads only the paginated objects both carry.
    // biome-ignore lint/plugin: see above.
    const contentsRequest = cancellable(
      httpGetCollectionsByCollectionIdContents(state.routeParams.params.id, {
        page: pageNumber,
        per_page: perPage,
        sort: getSortQuery(state.pages.search.list.sort),
      }),
    ) as unknown as typeof searchRequest;

    return dispatch(
      state.routeParams.route !== SHARE_COLLECTION_PAGE_ROUTE
        ? searchRequest
        : contentsRequest,
    );
  };

const fetchSearchList = <Result extends EndpointResponseNormalized>({
  dispatch,
  getState,
  getList,
  debounce,
  params,
}: {
  dispatch: AppDispatch;
  getState: GetState;
  getList: (
    dispatch: AppDispatch,
    params: CommonListGetParams,
  ) => Promise<Result>;
  debounce?: boolean;
  params?: CommonListGetParams;
}) =>
  commonGetList({
    listName: SEARCH_LIST_NAME,
    dispatch,
    params: {
      initialFacetsValues: getState().panelsState.searchFilters.facetValues,
      initialFacets: getState().panelsState.searchFilters.availableFacets,
      ...params,
    },
    config: {
      debounce,
      paginationType: selectSearchPaginationType(getState()),
      debounceDependencies: [
        SearchHttpPostSearch,
        AssetsHttpGetCollectionsByCollectionIdContents,
      ],
    },
    getList,
  });

const getSearchListPage = (
  dispatch: AppDispatch,
  params: CommonListGetParams,
) => dispatch(requestSearchItems(params));

/**
 * Fetches the search results again, as the panel and the URL now describe
 * them. Natural-search results are fetched as more of their answer, so a filter
 * edit, a page or a refresh stays on it. Anything else, including a natural
 * search that left nothing to replay, runs the regular search, which hands the
 * results back from any natural search first.
 */
export const getSearchItems =
  (debounce?: boolean) => (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    if (
      selectIsNaturalSearchActive(state) &&
      state.naturalSearch.session?.replay
    ) {
      return dispatch(refreshNaturalSearch({ debounce }));
    }
    dispatch(endNaturalSearchSession());
    return fetchSearchList({
      dispatch,
      getState,
      debounce,
      getList: getSearchListPage,
    });
  };

export const canResumeSearchList =
  () => (_dispatch: AppDispatch, getState: GetState) =>
    selectCanResumeSearchList(getState());

export const reloadSearchItems =
  (debounce?: boolean) => (dispatch: AppDispatch, getState: GetState) => {
    if (!selectIsSearchPagedByNumber(getState())) {
      dispatch(searchActions.resetItems());
    }
    return dispatch(getSearchItems(debounce));
  };

export const resetSearchListOnLeave =
  () => (dispatch: AppDispatch, getState: GetState) => {
    if (selectIsSearchPagedByNumber(getState())) {
      dispatch(searchActions.resetItems());
    }
  };

export const getMoreSearchItems =
  () => (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    if (selectIsNaturalSearchActive(state)) {
      return dispatch(loadMoreNaturalSearch());
    }
    const { fetchStatus, error, ids, nextSearchAfter, pagination } =
      state.pages.search.list;

    if (fetchStatus === 'fetching' || error) {
      return Promise.resolve(undefined);
    }

    const paginationType = selectSearchPaginationType(state);

    if (paginationType === 'regular') {
      return Promise.resolve(undefined);
    }

    if (paginationType === 'cursor') {
      const isComplete = pagination.total > 0 && ids.length >= pagination.total;
      if (isComplete) {
        return Promise.resolve(undefined);
      }
      if (!nextSearchAfter) {
        return ids.length > 0 && pagination.page < pagination.pages
          ? dispatch(reloadSearchItems())
          : Promise.resolve(undefined);
      }
      return fetchSearchList({
        dispatch,
        getState,
        params: { search_after: nextSearchAfter },
        getList: getSearchListPage,
      });
    }

    if (pagination.page >= pagination.pages) {
      return Promise.resolve(undefined);
    }
    return fetchSearchList({
      dispatch,
      getState,
      params: { page: pagination.page + 1 },
      getList: getSearchListPage,
    });
  };

export type GetCollectionItemsOptions = {
  collectionId: string;
  route: string;
};

export const getCollectionItems =
  ({ collectionId, route }: GetCollectionItemsOptions) =>
  (dispatch: AppDispatch, getState: GetState) => {
    dispatch(getEntityCurrentAcls(OBJECT_TYPE_COLLECTIONS, collectionId));
    return fetchSearchList({
      dispatch,
      getState,
      getList: async (_dispatch, params) => {
        const { body } = await dispatch(
          httpGetCollectionsByCollectionId(collectionId),
        );
        if (
          forceCustomSortRoutes.has(route) &&
          body.custom_order_status === 'ENABLED'
        ) {
          dispatch(searchActions.setSort(CUSTOM_ORDER));
        }
        dispatch(getCollectionParents(collectionId));
        return dispatch(requestSearchItems(params));
      },
    });
  };

export type GetSavedSearchItemsOptions = {
  savedSearchId: string;
  urlSearchQuery?: string;
};

/**
 * Loads a saved search, applies its criteria to the search filters and fetches
 * the results, as one tracked list fetch.
 */
export const getSavedSearchItems =
  ({ savedSearchId, urlSearchQuery }: GetSavedSearchItemsOptions) =>
  (dispatch: AppDispatch, getState: GetState) =>
    fetchSearchList({
      dispatch,
      getState,
      getList: async (_dispatch, params) => {
        const { body } = await dispatch(
          httpGetSearchSavedBySearchId(savedSearchId, {
            include_results: false,
          }),
        );
        // Also fetch the saved search object to entities since that's needed for context menus and modals.
        await dispatch(httpGetSearchSaved({ ids: savedSearchId }));

        const criteria = body.search_criteria_document?.criteria;

        const metadataFieldNames = collectMetadataFieldNames(criteria?.filter);

        // in a saved search we don't know the metadata view that was used to filter,
        // so we just fetch the fields
        if (metadataFieldNames.size > 0) {
          const fieldFilter = [...metadataFieldNames].join(',');
          await dispatch(
            getMetadataFields(false, { filter: fieldFilter }),
          ).catch(() => {});
        }

        const { filterValues, unsupportedFilters } = criteriaToFilterValues(
          criteria,
          undefined,
          selectMetadataMultiSelectFieldNames(getState()),
        );

        dispatch(searchFilterActions.setDisabledFilters([]));
        dispatch(searchFilterActions.setFilterValues(filterValues));
        dispatch(searchFilterActions.setUnsupportedFilters(unsupportedFilters));
        dispatch(
          searchFilterActions.setFacetValues(
            criteriaToFacetValues(criteria?.facets_filters ?? []),
          ),
        );
        dispatch(
          searchFilterActions.setQuery(urlSearchQuery || criteria?.query || ''),
        );
        if (criteria?.sort) {
          const sortOptions = criteria.sort.map((sortItem) => ({
            name: sortItem.name,
            order: toSortOrder(sortItem.order),
          }));
          dispatch(searchActions.setSort(sortOptions));
        }
        // Last: setQuery, setFilterValues and setFacetValues each clear the raw
        // criteria, and getSearchRequestBody short-circuits on it.
        if (criteria && !urlSearchQuery) {
          dispatch(searchFilterActions.setRawCriteria(criteria));
        }
        return dispatch(requestSearchItems(params));
      },
    });

export const setDisplayFieldsAndRefetch =
  (fields: SearchSearchViewFieldTypeSchema[]) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const previousFieldNames = selectVisibleSearchFieldNames(getState());
    dispatch(searchActions.setSearchDisplayFields(fields));
    const nextFieldNames = selectVisibleSearchFieldNames(getState());

    const hasNewFields = nextFieldNames.some(
      (name) => !previousFieldNames.includes(name),
    );
    // Natural results are whole documents, so a new column has nothing to
    // fetch, and refetching would hand the results back to the regular search.
    if (hasNewFields && !selectIsNaturalSearchActive(getState())) {
      dispatch(reloadSearchItems());
    }
  };

export const updateCollectionTitle =
  (collectionId: string, title: string) => (dispatch: AppDispatch) =>
    dispatch(
      httpPutCollectionsByCollectionId(collectionId, {
        title,
      }),
    ).then(() => {
      dispatch(
        modifyBranchByID(ID_COLLECTION, collectionId, {
          title,
        }),
      );
    });

export const updateSavedSearchTitle =
  (savedSearchId: string, title: string) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    return dispatch(
      updateSavedSearch(savedSearchId, {
        name: title,
        criteria:
          state.entities.saved_search_results[savedSearchId]
            ?.search_criteria_document?.criteria,
      }),
    );
  };

export const getFacets =
  () => async (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    return dispatch(
      cancellable(
        httpPostSearch(getSearchRequestBody(state), {
          per_page: 0,
          save_search_history: false,
        }),
        `${SearchHttpPostSearch}Facets`,
      ),
    );
  };

type UrlArgs = {
  page: number;
  per_page: number;
  search: string;
};

type UrlArgsObject = Record<string, string | number | undefined>;

const parsedUrlsEqual = (urlA: UrlArgsObject, urlB: UrlArgsObject) => {
  const keysA = Object.keys(urlA).filter((key) => urlA[key] !== undefined);
  const keysB = Object.keys(urlB).filter((key) => urlB[key] !== undefined);

  // Check if they have the same number of keys
  if (keysA.length !== keysB.length) {
    return false;
  }

  // Check if objects have the same keys and values
  for (const key of keysA) {
    if (String(urlA[key]) !== String(urlB[key])) {
      return false;
    }
  }

  return true;
};

export const updateUrlArgs =
  (urlArgs: Partial<UrlArgs>) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const searchParams = state.router.location.search;
    const parsedQuery = Object.fromEntries(new URLSearchParams(searchParams));
    const updatedQuery = {
      ...parsedQuery,
      page: urlArgs.page ?? getQueryPage(searchParams),
      per_page:
        urlArgs.per_page ?? getQueryPerPage(searchParams, PAGE_SIZE_ITEM),
      search: (urlArgs.search ?? getQuerySearch(searchParams)) || undefined,
    };

    if (parsedUrlsEqual(updatedQuery, parsedQuery)) {
      return;
    }

    dispatch(
      historyPush(
        `${state.router.location.pathname}?${qs.stringify(updatedQuery)}`,
      ),
    );
  };
